import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import {
  ChartLineUp,
  Users,
  PiggyBank,
  HandCoins,
  Buildings,
  UserGear,
  ShieldCheck,
  SignOut,
  Wallet,
  Receipt,
  ClipboardText,
  Gear,
  DeviceMobile,
  HourglassMedium,
  CheckSquareOffset,
  FileArrowDown,
  Trash,
} from "@phosphor-icons/react";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLineUp, testId: TID.navDashboard, roles: ["super_admin", "branch_admin", "agent", "staff"] },
  { to: "/customers", label: "Customers", icon: Users, testId: TID.navCustomers, roles: ["super_admin", "branch_admin", "agent", "staff"] },
  { to: "/savings", label: "Savings", icon: PiggyBank, testId: TID.navSavings, roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/savings/maturity", label: "Maturity", icon: HourglassMedium, testId: "nav-maturity", key: "maturity", roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/loans", label: "Loans", icon: HandCoins, testId: TID.navLoans, roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/loans/closure", label: "Loan Closure", icon: CheckSquareOffset, testId: "nav-loan-closure", key: "loan_closure", roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/loan-requests", label: "Raise Loan Request", icon: HandCoins, testId: "nav-loan-requests", key: "loan_requests", roles: ["super_admin", "branch_admin", "agent", "staff"] },
  { to: "/collections", label: "Collections", icon: Wallet, testId: TID.navCollections, roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/expenses", label: "Expenses", icon: Receipt, testId: "nav-expenses", roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/edit-requests", label: "Edit Requests", icon: ClipboardText, testId: "nav-edit-requests", key: "edit_requests", roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/agent/collect", label: "Field Collect", icon: DeviceMobile, testId: TID.navAgentCollect, roles: ["super_admin", "branch_admin", "agent"] },
  { to: "/branches", label: "Branches", icon: Buildings, testId: TID.navBranches, roles: ["super_admin"] },
  { to: "/users", label: "Users", icon: UserGear, testId: TID.navUsers, roles: ["super_admin", "branch_admin"] },
  { to: "/permissions", label: "Permissions", icon: ShieldCheck, testId: TID.navPermissions, roles: ["super_admin"] },
  { to: "/reports", label: "Reports", icon: FileArrowDown, testId: "nav-reports", roles: ["super_admin", "branch_admin", "staff"] },
  { to: "/settings", label: "Settings", icon: Gear, testId: "nav-settings", roles: ["super_admin", "branch_admin", "agent", "staff"] },
  { to: "/reset", label: "Reset Data", icon: Trash, testId: "nav-reset", roles: ["super_admin"] },
];

const roleBadge = {
  super_admin: "bg-emerald-50 text-emerald-700 border-emerald-200",
  branch_admin: "bg-sky-50 text-sky-700 border-sky-200",
  agent: "bg-amber-50 text-amber-700 border-amber-200",
  staff: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [pendingEdits, setPendingEdits] = useState(0);
  const [alerts, setAlerts] = useState({ maturity: 0, loan_closure: 0 });
  const [loanReqCount, setLoanReqCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function fetchPending() {
      if (!user || !["super_admin", "branch_admin"].includes(user.role)) return;
      try {
        const { data } = await api.get("/edit-requests?status=pending&limit=200");
        if (!cancelled) setPendingEdits(Array.isArray(data) ? data.length : 0);
      } catch { /* ignore */ }
    }
    async function fetchAlertCounts() {
      if (!user) return;
      try {
        const { data } = await api.get("/dashboard/summary");
        if (cancelled) return;
        setAlerts({
          maturity: (data?.maturing_soon_count || 0) + (data?.matured_pending_count || 0),
          loan_closure: data?.loan_closure_alerts || 0,
        });
      } catch { /* ignore */ }
      try {
        const { data: lrs } = await api.get("/loan-requests?status=requested&limit=200");
        if (cancelled) return;
        setLoanReqCount(Array.isArray(lrs) ? lrs.length : 0);
      } catch { /* ignore */ }
    }
    fetchPending();
    fetchAlertCounts();
    const t = setInterval(() => { fetchPending(); fetchAlertCounts(); }, 30000);
    const onChanged = () => fetchPending();
    window.addEventListener("edit-requests-changed", onChanged);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("edit-requests-changed", onChanged);
    };
  }, [user]);

  if (!user) return null;

  const isAgent = user.role === "agent";
  const links = NAV.filter((n) => n.roles.includes(user.role));
  const bottomLinks = isAgent ? links : links.slice(0, 4);

  const renderBadge = (n) => {
    if (n.key === "edit_requests" && pendingEdits > 0) {
      return <span className="ml-auto text-[10px] font-semibold bg-amber-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="nav-edit-requests-badge">{pendingEdits}</span>;
    }
    if (n.key === "maturity" && alerts.maturity > 0) {
      return <span className="ml-auto text-[10px] font-semibold bg-amber-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center blink-alert" data-testid="nav-maturity-badge">{alerts.maturity}</span>;
    }
    if (n.key === "loan_closure" && alerts.loan_closure > 0) {
      return <span className="ml-auto text-[10px] font-semibold bg-rose-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center blink-alert" data-testid="nav-loan-closure-badge">{alerts.loan_closure}</span>;
    }
    if (n.key === "loan_requests" && loanReqCount > 0) {
      return <span className="ml-auto text-[10px] font-semibold bg-emerald-600 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center blink-alert" data-testid="nav-loan-requests-badge">{loanReqCount}</span>;
    }
    return null;
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside
        data-testid={TID.sidebar}
        className={`${isAgent ? "hidden" : "hidden md:flex md:w-64"} flex-col bg-white border-r border-slate-200 sticky top-0 h-screen`}
      >
        <div className="px-6 py-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl brand-gradient flex items-center justify-center shrink-0">
              <PiggyBank weight="fill" className="text-white" size={22} />
            </div>
            <div className="min-w-0">
              <div className="heading text-sm font-semibold text-slate-900 leading-tight">Ujjwal Multipurpose</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-medium mt-0.5">Cooperative Society Ltd</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {links.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-800"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon size={18} weight="regular" />
              <span>{n.label}</span>
              {renderBadge(n)}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-semibold">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user.name}</div>
              <span
                className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full border font-medium mt-0.5 ${roleBadge[user.role]}`}
              >
                {ROLE_LABEL[user.role]}
              </span>
            </div>
          </div>
          <button
            data-testid={TID.logoutBtn}
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className={`${isAgent ? "" : "md:hidden"} fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40 px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg brand-gradient flex items-center justify-center shrink-0">
            <PiggyBank weight="fill" className="text-white" size={16} />
          </div>
          <div className="heading font-semibold text-sm truncate">Ujjwal Multipurpose</div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
          <button
            data-testid={TID.logoutBtn + "-mobile"}
            onClick={async () => { await logout(); nav("/login"); }}
            className="text-sm text-slate-600 p-1"
          >
            <SignOut size={20} />
          </button>
        </div>
      </div>

      <main className={`flex-1 ${isAgent ? "pt-14 max-w-md mx-auto w-full" : "md:pt-0 pt-14"} min-w-0 flex flex-col`}>
        {/* Desktop top strip with the notifications bell */}
        <div className={`${isAgent ? "hidden" : "hidden md:flex"} sticky top-0 z-30 bg-slate-50/80 backdrop-blur border-b border-slate-200 px-6 py-3 justify-end`}>
          <NotificationsBell />
        </div>
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
        {/* Mobile bottom nav for critical actions */}
        <div
          className={`${isAgent ? "" : "md:hidden"} fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 grid ${isAgent ? "max-w-md mx-auto grid-cols-5" : "grid-cols-4"}`}
        >
          {bottomLinks.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId + "-mobile"}
              className={({ isActive }) =>
                `flex flex-col items-center py-2 text-[11px] ${isActive ? "text-emerald-700" : "text-slate-500"}`
              }
            >
              <n.icon size={20} weight="regular" />
              <span className="mt-0.5 text-center leading-tight px-0.5">{n.label}</span>
            </NavLink>
          ))}
        </div>
      </main>
    </div>
  );
}
